import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MediaPicker from '../../../components/admin/MediaPicker';
import { ToolSectionRenderer } from '../../tools/ToolPageSections';
import { ToolPageSectionFields } from './ToolPageSectionFields';
import { SECTION_TYPES, defaultSection, getSectionSummary, getSectionTypeMeta } from './shared';
import '../../../styles.css';

function InsertSectionButton({ onAdd, label = 'Add section' }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="tpb-insert-zone">
      <button type="button" className="tpb-insert-btn" onClick={() => setOpen((v) => !v)}>
        <i className="fas fa-plus" aria-hidden="true" />
        {label}
      </button>
      {open && (
        <div className="tpb-insert-menu">
          {SECTION_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className="tpb-insert-menu-item"
              onClick={() => {
                onAdd(t.value);
                setOpen(false);
              }}
            >
              <i className={t.icon} aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewSectionBlock({
  section,
  index,
  total,
  selected,
  onSelect,
  onMove,
  onRemove,
  onDuplicate,
}) {
  const ref = useRef(null);
  const meta = getSectionTypeMeta(section.type);

  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selected]);

  return (
    <div
      ref={ref}
      className={`tpb-preview-block${selected ? ' is-selected' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(index);
      }}
    >
      <div className="tpb-preview-block-label">
        <i className={meta.icon} aria-hidden="true" />
        <span>{meta.label}</span>
        <span className="tpb-preview-block-num">#{index + 1}</span>
      </div>
      {selected && (
        <div className="tpb-preview-block-toolbar">
          <button type="button" className="admin-icon-btn" title="Move up" disabled={index === 0} onClick={(e) => { e.stopPropagation(); onMove(index, -1); }}>
            <i className="fas fa-arrow-up" aria-hidden="true" />
          </button>
          <button type="button" className="admin-icon-btn" title="Move down" disabled={index === total - 1} onClick={(e) => { e.stopPropagation(); onMove(index, 1); }}>
            <i className="fas fa-arrow-down" aria-hidden="true" />
          </button>
          <button type="button" className="admin-icon-btn" title="Duplicate" onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}>
            <i className="fas fa-copy" aria-hidden="true" />
          </button>
          <button type="button" className="admin-icon-btn danger" title="Remove section" onClick={(e) => { e.stopPropagation(); onRemove(index); }}>
            <i className="fas fa-trash" aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="tpb-preview-block-content">
        <ToolSectionRenderer section={section} />
      </div>
    </div>
  );
}

export default function ToolPageVisualEditor({
  form,
  setForm,
  editingSlug,
  message,
  onSave,
  onClose,
  onShowJson,
}) {
  const [sidebarTab, setSidebarTab] = useState('sections');
  const [selectedSection, setSelectedSection] = useState(null);
  const [heroSelected, setHeroSelected] = useState(false);

  const sections = form.content?.sections || [];

  const updateSection = (index, nextSection) => {
    const next = [...sections];
    next[index] = nextSection;
    setForm({ ...form, content: { sections: next } });
  };

  const addSection = (type, atIndex = sections.length) => {
    const next = [...sections];
    next.splice(atIndex, 0, defaultSection(type));
    setForm({ ...form, content: { sections: next } });
    setSelectedSection(atIndex);
    setHeroSelected(false);
    setSidebarTab('sections');
  };

  const removeSection = (index) => {
    const next = sections.filter((_, i) => i !== index);
    setForm({ ...form, content: { sections: next } });
    if (selectedSection === index) setSelectedSection(null);
    else if (selectedSection > index) setSelectedSection(selectedSection - 1);
  };

  const moveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    setForm({ ...form, content: { sections: next } });
    setSelectedSection(target);
  };

  const duplicateSection = (index) => {
    const clone = JSON.parse(JSON.stringify(sections[index]));
    const next = [...sections];
    next.splice(index + 1, 0, clone);
    setForm({ ...form, content: { sections: next } });
    setSelectedSection(index + 1);
  };

  const selectSection = (index) => {
    setSelectedSection(index);
    setHeroSelected(false);
    setSidebarTab('sections');
  };

  const selectHero = () => {
    setHeroSelected(true);
    setSelectedSection(null);
    setSidebarTab('hero');
  };

  const clearSelection = () => {
    setSelectedSection(null);
    setHeroSelected(false);
  };

  useEffect(() => {
    document.body.classList.add('tpb-editor-active');
    return () => document.body.classList.remove('tpb-editor-active');
  }, []);

  const editor = (
    <div className="tpb-shell">
      <div className="tpb-topbar" role="banner">
        <div className="tpb-topbar-left">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back
          </button>
          <div className="tpb-topbar-title">
            <strong>{editingSlug ? form.title || editingSlug : 'New tool page'}</strong>
            {editingSlug && <span>/tools/{editingSlug}</span>}
          </div>
        </div>
        <div className="tpb-topbar-right">
          {message && <span className="tpb-topbar-message">{message}</span>}
          <label className="admin-checkbox tpb-published-toggle">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Published
          </label>
          <button type="button" className="btn btn-secondary" onClick={onShowJson}>JSON</button>
          {editingSlug && (
            <a href={`/tools/${editingSlug}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> Live preview
            </a>
          )}
          <button type="button" className="btn btn-primary" onClick={onSave}>
            <i className="fas fa-save" aria-hidden="true" /> {editingSlug ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <div className="tpb-layout">
        <aside className="tpb-sidebar">
          <div className="tpb-sidebar-tabs">
            <button type="button" className={`tpb-sidebar-tab${sidebarTab === 'sections' ? ' is-active' : ''}`} onClick={() => setSidebarTab('sections')}>
              Sections
            </button>
            <button type="button" className={`tpb-sidebar-tab${sidebarTab === 'hero' ? ' is-active' : ''}`} onClick={() => { setSidebarTab('hero'); setHeroSelected(true); setSelectedSection(null); }}>
              Hero
            </button>
            <button type="button" className={`tpb-sidebar-tab${sidebarTab === 'page' ? ' is-active' : ''}`} onClick={() => setSidebarTab('page')}>
              Page
            </button>
            <button type="button" className={`tpb-sidebar-tab${sidebarTab === 'seo' ? ' is-active' : ''}`} onClick={() => setSidebarTab('seo')}>
              SEO
            </button>
          </div>

          <div className="tpb-sidebar-body">
            {sidebarTab === 'sections' && (
              <>
                {selectedSection !== null && sections[selectedSection] ? (
                  <div className="tpb-sidebar-panel admin-form">
                    <div className="tpb-sidebar-panel-head">
                      <button type="button" className="tpb-back-btn" onClick={clearSelection}>
                        <i className="fas fa-chevron-left" aria-hidden="true" />
                      </button>
                      <div>
                        <span className="tpb-sidebar-kicker">Editing section {selectedSection + 1}</span>
                        <strong>{getSectionSummary(sections[selectedSection])}</strong>
                      </div>
                    </div>
                    <ToolPageSectionFields
                      section={sections[selectedSection]}
                      onChange={(next) => updateSection(selectedSection, next)}
                    />
                  </div>
                ) : (
                  <>
                    <p className="tpb-sidebar-hint">Click a section in the preview, or pick one from the list below.</p>
                    <div className="tpb-section-palette">
                      {SECTION_TYPES.map((t) => (
                        <button key={t.value} type="button" className="tpb-palette-btn" onClick={() => addSection(t.value)}>
                          <i className={t.icon} aria-hidden="true" />
                          <span>{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {selectedSection === null && (
                <div className="tpb-section-nav">
                  <div className="tpb-section-nav-head">
                    <span>Page outline</span>
                    <span>{sections.length} sections</span>
                  </div>
                  <button
                    type="button"
                    className={`tpb-outline-item${heroSelected ? ' is-active' : ''}`}
                    onClick={selectHero}
                  >
                    <i className="fas fa-image" aria-hidden="true" />
                    <span>Hero banner</span>
                  </button>
                  {sections.map((section, idx) => {
                    const meta = getSectionTypeMeta(section.type);
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`tpb-outline-item${selectedSection === idx ? ' is-active' : ''}`}
                        onClick={() => selectSection(idx)}
                      >
                        <i className={meta.icon} aria-hidden="true" />
                        <span className="tpb-outline-label">
                          <span className="tpb-outline-num">{idx + 1}.</span>
                          {getSectionSummary(section)}
                        </span>
                      </button>
                    );
                  })}
                  {sections.length === 0 && (
                    <p className="tpb-outline-empty">No sections yet — add one above or in the preview.</p>
                  )}
                </div>
                )}
              </>
            )}

            {sidebarTab === 'hero' && (
              <div className="tpb-sidebar-panel admin-form">
                <h4>Hero banner</h4>
                <div className="admin-grid-2">
                  <label>
                    Title before highlight
                    <input value={form.hero.title} onChange={(e) => setForm({ ...form, hero: { ...form.hero, title: e.target.value } })} />
                  </label>
                  <label>
                    Highlighted word
                    <input value={form.hero.highlight} onChange={(e) => setForm({ ...form, hero: { ...form.hero, highlight: e.target.value } })} />
                  </label>
                </div>
                <label>
                  Subtitle
                  <textarea value={form.hero.subtitle} onChange={(e) => setForm({ ...form, hero: { ...form.hero, subtitle: e.target.value } })} rows={2} />
                </label>
                <MediaPicker
                  label="Hero / preview image"
                  value={form.hero.image}
                  onChange={(url) => setForm({ ...form, hero: { ...form.hero, image: url } })}
                />
                {form.hero.image && (
                  <div className="tpb-media-preview">
                    <img src={form.hero.image} alt={form.title || 'Hero preview'} />
                  </div>
                )}
              </div>
            )}

            {sidebarTab === 'page' && (
              <div className="tpb-sidebar-panel admin-form">
                <h4>Page settings</h4>
                <label>
                  Slug (URL path)
                  <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!!editingSlug} placeholder="my-new-tool" required />
                </label>
                <label>
                  Page title
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                </label>
                <label>
                  Sort order
                  <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
                </label>
              </div>
            )}

            {sidebarTab === 'seo' && (
              <div className="tpb-sidebar-panel admin-form">
                <h4>SEO</h4>
                <label>
                  Description
                  <textarea value={form.seo.description} onChange={(e) => setForm({ ...form, seo: { ...form.seo, description: e.target.value } })} rows={3} />
                </label>
                <label>
                  Keywords
                  <input value={form.seo.keywords} onChange={(e) => setForm({ ...form, seo: { ...form.seo, keywords: e.target.value } })} />
                </label>
                <label>
                  Canonical URL
                  <input value={form.seo.url} onChange={(e) => setForm({ ...form, seo: { ...form.seo, url: e.target.value } })} />
                </label>
              </div>
            )}
          </div>
        </aside>

        <div className="tpb-canvas-wrap">
          <div className="tpb-canvas-toolbar">
            <span><i className="fas fa-eye" aria-hidden="true" /> Live preview</span>
            <span className="tpb-canvas-hint">Click any block to edit it</span>
          </div>
          <div className="tpb-canvas-scroll" onClick={clearSelection}>
            <div className="tpb-canvas-page">
              <div
                className={`tpb-preview-hero${heroSelected ? ' is-selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  selectHero();
                }}
              >
                <div className="tpb-preview-block-label tpb-preview-block-label--hero">
                  <i className="fas fa-image" aria-hidden="true" />
                  <span>Hero</span>
                </div>
                <section className="trophy-tracker-hero">
                  <div className="container">
                    <h1>
                      {form.hero.title || 'Page title'}
                      {form.hero.highlight && <span className="highlight"> {form.hero.highlight}</span>}
                    </h1>
                    {form.hero.subtitle ? <p>{form.hero.subtitle}</p> : <p className="tpb-placeholder">Hero subtitle…</p>}
                  </div>
                </section>
              </div>

              <section className="trophy-tracker-content">
                <div className="container">
                  {form.hero.image && (
                    <div className="trophy-tracker-image">
                      <img src={form.hero.image} alt={form.title || 'Preview'} />
                    </div>
                  )}

                  <InsertSectionButton onAdd={(type) => addSection(type, 0)} label="Insert section here" />

                  {sections.map((section, idx) => (
                    <React.Fragment key={idx}>
                      <PreviewSectionBlock
                        section={section}
                        index={idx}
                        total={sections.length}
                        selected={selectedSection === idx}
                        onSelect={selectSection}
                        onMove={moveSection}
                        onRemove={removeSection}
                        onDuplicate={duplicateSection}
                      />
                      <InsertSectionButton onAdd={(type) => addSection(type, idx + 1)} />
                    </React.Fragment>
                  ))}

                  {sections.length === 0 && (
                    <div className="tpb-empty-canvas">
                      <i className="fas fa-layer-group" aria-hidden="true" />
                      <p>Your page is empty</p>
                      <span>Add sections from the left panel or use the button above.</span>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(editor, document.body);
}
